import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import type {
  UseListPageOptions,
  UseListPageResult,
} from '../types/listPageTypes';
import { useFilterUrlState } from './useFilterUrlState';
import { useDebouncedValue } from './useDebouncedValue';
import { DEFAULT_ROWS_PER_PAGE } from '../constants/pagination';

export const LIST_SEARCH_DEBOUNCE_MS = 300;

/**
 * Shared state + handlers for Dashboard V2 list pages
 * (Stations, Sessions, Transactions, Users).
 *
 * Filter changes reset pagination to page 0. Refresh invalidates
 * all queries under the given root key. When `urlSync` is true,
 * filters + page persist to URL search params.
 */
export function useListPage<F>({
  initialFilters,
  queryKeyRoot,
  initialRowsPerPage = DEFAULT_ROWS_PER_PAGE,
  urlSync = false,
  numericFilterKeys,
  arrayFilterKeys,
}: UseListPageOptions<F>): UseListPageResult<F> {
  const queryClient = useQueryClient();

  // -- Local-only path --
  const [localFilters, setLocalFilters] = useState<F>(initialFilters);
  const [localPage, setLocalPage] = useState(0);
  const [localRowsPerPage, setLocalRowsPerPage] = useState(initialRowsPerPage);

  // -- URL-synced path --
  // Cast: `useFilterUrlState` requires a flat record shape. Callers using
  // urlSync are responsible for keeping their filter shape flat (string |
  // number | boolean | array | null/undefined values only).
  type AnyFilterShape = Record<
    string,
    string | number | boolean | (string | number | boolean)[] | null | undefined
  >;
  const [urlFilters, urlSetFilters, urlResetFilters] = useFilterUrlState<AnyFilterShape>({
    initial: initialFilters as unknown as AnyFilterShape,
    numericKeys: numericFilterKeys as string[] | undefined,
    arrayKeys: arrayFilterKeys as string[] | undefined,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = Number(searchParams.get('page') ?? '0') || 0;
  const urlRowsPerPage = Number(searchParams.get('pageSize') ?? '') || initialRowsPerPage;

  // -- Active values --
  const filters = (urlSync ? (urlFilters as F) : localFilters) as F;
  const page = urlSync ? urlPage : localPage;
  const rowsPerPage = urlSync ? urlRowsPerPage : localRowsPerPage;

  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);

  // Server queries get `search` debounced + trimmed so typing doesn't fire a
  // request per keystroke; everything else passes through unchanged.
  const searchValue = (filters as { search?: unknown }).search;
  const debouncedSearch = useDebouncedValue(
    typeof searchValue === 'string' ? searchValue.trim() : undefined,
    LIST_SEARCH_DEBOUNCE_MS,
  );
  const queryFilters = useMemo<F>(() => {
    if (typeof searchValue !== 'string') return filters;
    return { ...filters, search: debouncedSearch } as F;
  }, [filters, searchValue, debouncedSearch]);

  const writePagination = useCallback(
    (nextPage: number, nextRowsPerPage?: number) => {
      setSearchParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          if (nextPage === 0) out.delete('page');
          else out.set('page', String(nextPage));
          if (nextRowsPerPage === initialRowsPerPage) out.delete('pageSize');
          else if (nextRowsPerPage != null) out.set('pageSize', String(nextRowsPerPage));
          return out;
        },
        { replace: true },
      );
    },
    [setSearchParams, initialRowsPerPage],
  );

  // -- Handlers --
  const setPage = useCallback(
    (next: number) => {
      if (urlSync) writePagination(next, rowsPerPage);
      else setLocalPage(next);
    },
    [urlSync, writePagination, rowsPerPage],
  );

  const handlePageChange = setPage;

  const handleRowsPerPageChange = useCallback(
    (next: number) => {
      if (urlSync) writePagination(0, next);
      else {
        setLocalRowsPerPage(next);
        setLocalPage(0);
      }
    },
    [urlSync, writePagination],
  );

  // Filter changes always reset page=0; we hand `urlSetFilters` a hook that
  // mutates the URLSearchParams in-place so filters + page reset land in a
  // single setSearchParams call (avoids a stale-prev race that previously
  // clobbered the new filter values).
  const resetPaginationParam = useCallback(
    (out: URLSearchParams) => {
      out.delete('page');
      if (rowsPerPage !== initialRowsPerPage) {
        out.set('pageSize', String(rowsPerPage));
      } else {
        out.delete('pageSize');
      }
    },
    [rowsPerPage, initialRowsPerPage],
  );

  const handleFilterChange = useCallback(
    (updates: Partial<F>) => {
      if (urlSync) {
        urlSetFilters(updates as unknown as Partial<AnyFilterShape>, resetPaginationParam);
      } else {
        setLocalFilters((prev) => ({ ...prev, ...updates }));
        setLocalPage(0);
      }
    },
    [urlSync, urlSetFilters, resetPaginationParam],
  );

  const resetFilters = useCallback(() => {
    if (urlSync) {
      urlResetFilters(resetPaginationParam);
    } else {
      setLocalFilters(initialFilters);
      setLocalPage(0);
    }
  }, [urlSync, urlResetFilters, resetPaginationParam, initialFilters]);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [queryKeyRoot] });
  }, [queryClient, queryKeyRoot]);

  const openFilterSheet = useCallback(() => setIsFilterSheetOpen(true), []);
  const closeFilterSheet = useCallback(() => setIsFilterSheetOpen(false), []);

  // Clamp pagination if URL drops below 0 (defensive)
  useEffect(() => {
    if (urlSync && urlPage < 0) writePagination(0, rowsPerPage);
  }, [urlSync, urlPage, rowsPerPage, writePagination]);

  return {
    page,
    rowsPerPage,
    filters,
    queryFilters,
    isFilterSheetOpen,
    setPage,
    handlePageChange,
    handleRowsPerPageChange,
    handleFilterChange,
    resetFilters,
    refresh,
    openFilterSheet,
    closeFilterSheet,
  };
}
